// ./src/config/vehicleTaskOptions.ts
// Vehicle task-related options configuration
// Used in selector components for task status, priority, category, and urgency display

import { VEHICLE_TASK_STATUS, VEHICLE_TASK_PRIORITY } from "@/utils/api";
import { SCHEDULE_TYPE } from "@/utils/api";

// ============================================
// Types
// ============================================

export interface VehicleTaskOption {
  /** Value stored in database */
  value: string | number;
  /** Translation key for label - use with t('vehicleTasks:status.todo') */
  labelKey: string;
  /** Icon name (Tabler icons) */
  icon?: string;
  /** Theme color */
  color?: string;
}

// ============================================
// Task Status Options
// ============================================

export const VEHICLE_TASK_STATUS_OPTIONS: VehicleTaskOption[] = [
  { value: VEHICLE_TASK_STATUS.TODO, labelKey: "status.todo", icon: "Circle", color: "gray" },
  { value: VEHICLE_TASK_STATUS.IN_PROGRESS, labelKey: "status.inProgress", icon: "Clock", color: "blue" },
  { value: VEHICLE_TASK_STATUS.COMPLETE, labelKey: "status.complete", icon: "CircleCheck", color: "teal" },
  { value: VEHICLE_TASK_STATUS.REMOVED, labelKey: "status.removed", icon: "CircleX", color: "red" },
];

export const VEHICLE_TASK_STATUS_MAP: Record<number, VehicleTaskOption> = Object.fromEntries(
  VEHICLE_TASK_STATUS_OPTIONS.map((opt) => [opt.value, opt])
);

// ============================================
// Task Priority Options
// ============================================

export const VEHICLE_TASK_PRIORITY_OPTIONS: VehicleTaskOption[] = [
  { value: VEHICLE_TASK_PRIORITY.LOW, labelKey: "priority.low", icon: "ArrowDown", color: "gray" },
  { value: VEHICLE_TASK_PRIORITY.MEDIUM, labelKey: "priority.medium", icon: "ArrowRight", color: "yellow" },
  { value: VEHICLE_TASK_PRIORITY.HIGH, labelKey: "priority.high", icon: "ArrowUp", color: "red" },
];

export const VEHICLE_TASK_PRIORITY_MAP: Record<number, VehicleTaskOption> = Object.fromEntries(
  VEHICLE_TASK_PRIORITY_OPTIONS.map((opt) => [opt.value, opt])
);

// ============================================
// Task Category Options
// ============================================

export const VEHICLE_TASK_CATEGORY_OPTIONS: VehicleTaskOption[] = [
  { value: "administrative", labelKey: "category.administrative", icon: "FileText", color: "blue" },
  { value: "purchase", labelKey: "category.purchase", icon: "ShoppingCart", color: "teal" },
  { value: "appointment", labelKey: "category.appointment", icon: "Calendar", color: "violet" },
  { value: "seasonal", labelKey: "category.seasonal", icon: "Leaf", color: "orange" },
  { value: "repair", labelKey: "category.repair", icon: "Tool", color: "red" },
];

export const VEHICLE_TASK_CATEGORY_MAP: Record<string, VehicleTaskOption> = Object.fromEntries(
  VEHICLE_TASK_CATEGORY_OPTIONS.map((opt) => [opt.value, opt])
);

// ============================================
// Task Schedule Type Options
// ============================================

export const VEHICLE_TASK_SCHEDULE_TYPE_OPTIONS: VehicleTaskOption[] = [
  { value: SCHEDULE_TYPE.WEEKLY, labelKey: "scheduleType.weekly", icon: "CalendarRepeat", color: "blue" },
  { value: SCHEDULE_TYPE.MONTHLY, labelKey: "scheduleType.monthly", icon: "CalendarMonth", color: "violet" },
  { value: SCHEDULE_TYPE.YEARLY, labelKey: "scheduleType.yearly", icon: "CalendarEvent", color: "teal" },
  { value: SCHEDULE_TYPE.ONE_TIME, labelKey: "scheduleType.oneTime", icon: "CalendarDot", color: "gray" },
];

export const VEHICLE_TASK_SCHEDULE_TYPE_MAP: Record<string, VehicleTaskOption> = Object.fromEntries(
  VEHICLE_TASK_SCHEDULE_TYPE_OPTIONS.map((opt) => [opt.value, opt])
);

// ============================================
// Task Urgency Status Options
// (Computed server-side, used for display only)
// ============================================

export const VEHICLE_TASK_URGENCY_OPTIONS: VehicleTaskOption[] = [
  { value: "overdue", labelKey: "urgency.overdue", icon: "AlertTriangle", color: "red" },
  { value: "due_soon", labelKey: "urgency.dueSoon", icon: "Clock", color: "orange" },
  { value: "in_progress", labelKey: "urgency.inProgress", icon: "Loader", color: "blue" },
  { value: "upcoming", labelKey: "urgency.upcoming", icon: "Calendar", color: "gray" },
  { value: "no_due_date", labelKey: "urgency.noDueDate", icon: "ListDetails", color: "gray" },
  { value: "complete", labelKey: "urgency.complete", icon: "Check", color: "green" },
];

export const VEHICLE_TASK_URGENCY_MAP: Record<string, VehicleTaskOption> = Object.fromEntries(
  VEHICLE_TASK_URGENCY_OPTIONS.map((opt) => [opt.value, opt])
);

// ============================================
// Helper Functions
// ============================================

export const getTaskStatusOption = (value: number | null | undefined): VehicleTaskOption | undefined => {
  if (value == null) return undefined;
  return VEHICLE_TASK_STATUS_MAP[value];
};

export const getTaskPriorityOption = (value: number | null | undefined): VehicleTaskOption | undefined => {
  if (value == null) return undefined;
  return VEHICLE_TASK_PRIORITY_MAP[value];
};

export const getTaskCategoryOption = (value: string | null | undefined): VehicleTaskOption | undefined => {
  if (!value) return undefined;
  return VEHICLE_TASK_CATEGORY_MAP[value];
};

export const getTaskScheduleTypeOption = (value: string | null | undefined): VehicleTaskOption | undefined => {
  if (!value) return undefined;
  return VEHICLE_TASK_SCHEDULE_TYPE_MAP[value];
};

export const getTaskUrgencyOption = (value: string | null | undefined): VehicleTaskOption | undefined => {
  if (!value) return undefined;
  return VEHICLE_TASK_URGENCY_MAP[value];
};